import {
  getMemberImportMapping,
  MemberImportMappingApiError,
  type MemberImportMappingEntry,
  type MemberImportMappingResponse,
} from '@urbanik/api-client';
import {
  DataTable,
  StatePanel,
  canonicalStates,
  formatDateTime,
  type DataColumn,
} from '@urbanik/ui';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import './member-import-diagnostic.css';
type State = 'loading' | 'success' | 'empty' | 'error' | 'forbidden';
const stateDefinition = (state: State) => canonicalStates.find((item) => item.kind === state)!;
const columns: readonly DataColumn<MemberImportMappingEntry>[] = [
  {
    id: 'sourceField',
    header: 'Campo original',
    accessor: (row) => row.sourceField,
    cell: (row) => <strong>{row.sourceField}</strong>,
  },
  { id: 'targetField', header: 'Campo normalizado', accessor: (row) => row.targetField },
  { id: 'originalValue', header: 'Valor original', accessor: (row) => row.originalValue },
  {
    id: 'normalizedValue',
    header: 'Valor normalizado',
    accessor: (row) => row.normalizedValue,
    cell: (row) => <code>{row.normalizedValue}</code>,
  },
  {
    id: 'occurrences',
    header: 'Apariciones',
    accessor: (row) => row.occurrences.toLocaleString('es-EC'),
  },
];
export function MemberImportMappingView() {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<State>('loading');
  const [mapping, setMapping] = useState<MemberImportMappingResponse | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getMemberImportMapping(apiBase, controller.signal)
      .then((result) => {
        setMapping(result);
        setState(result.mappings.length ? 'success' : 'empty');
      })
      .catch((error: unknown) =>
        setState(
          error instanceof MemberImportMappingApiError && error.kind === 'forbidden'
            ? 'forbidden'
            : 'error',
        ),
      );
    return () => controller.abort();
  }, [attempt]);
  if (state !== 'success' || !mapping)
    return state === 'error' ? (
      <StatePanel
        state={stateDefinition(state)}
        onAction={() => setAttempt((value) => value + 1)}
      />
    ) : (
      <StatePanel state={stateDefinition(state)} />
    );
  return (
    <div className="shell-page import-diagnostic-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M04.2 · Mapeo original a normalizado</p>
          <h1>Cada transformación conserva su origen.</h1>
          <p>
            Catálogo agregado de campos y estados de la fuente histórica. No persiste socios ni
            presenta filas para importar.
          </p>
        </div>
        <span className="surface-badge">Solo lectura</span>
      </header>
      <section className="diagnostic-file" aria-label="Fuente del mapeo">
        <div>
          <span>Hoja fuente</span>
          <strong>{mapping.sourceSheet}</strong>
          <small>Encabezados en fila {mapping.headerRow}</small>
        </div>
        <div>
          <span>Generado</span>
          <strong>{formatDateTime(mapping.generatedAt)}</strong>
          <small>
            {mapping.dataRowCount.toLocaleString('es-EC')} filas de origen contabilizadas
          </small>
        </div>
      </section>
      <section aria-labelledby="mapping-title">
        <div className="section-title">
          <p className="kicker">Evidencia reproducible</p>
          <h2 id="mapping-title">Original → normalizado</h2>
        </div>
        <DataTable
          columns={columns}
          rows={mapping.mappings}
          visibleColumnIds={new Set(columns.map((column) => column.id))}
          sort={{ columnId: 'sourceField', direction: 'ascending' }}
          onSort={() => {}}
          empty={<StatePanel state={stateDefinition('empty')} />}
        />
      </section>
    </div>
  );
}
